import { Component, ElementRef, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, map, Subject, Subscription } from 'rxjs';
import { NgSelectModule } from '@ng-select/ng-select';
import { IAvSettingSelectOption, IAvSettingsItemConfig, IAvSettingsItemConfigEventString } from '../../interfaces/av-producer/event-av-producer.interface';
import { AvProdSettingsType } from '../../const/av-producer.const';
import { AV_PROD_OPTIONS_COLOR } from '../../const/av-producer-options';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'web-settings-item-shared',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule, ReactiveFormsModule, TranslateModule],
  templateUrl: './settings-item-shared.component.html',
  styleUrl: './settings-item-shared.component.scss',
})

export class SettingsItemSharedComponent implements OnInit, OnDestroy {

  //protected value: number | string | undefined = undefined;
  protected formControl: AbstractControl = new FormControl();
  protected colorText: string = '';
  protected colorCustom: string = '#000000';
  protected _isDisabled: boolean | undefined;
  // Subject to capture search texts of ng-select
  protected searchInputSubject$ = new Subject<string>();

  protected colorValueSubscription: Subscription = new Subscription();

  protected translatedSelectNotfound: string = 'general.selectNotFound';
  protected translatedSelectTypeToSearch: string = 'general.selectTypeToSearch';
  protected translatedSelectAddTag: string = 'general.selectAddTag';

  protected languageSubscription: Subscription | undefined;

  protected valueOption: IAvSettingSelectOption = {
    label: '',
    labelTranslate: '',
    valueNumber: 0,
    valueStr: ''
  };

  protected configInt: IAvSettingsItemConfig = {
    id: '',
    type: AvProdSettingsType.none,
    title: '',
    name: '',
    min: 0,
    max: 0,
    step: 1,
    options: [],
    placeholder: '',
    value: ''
  }

  @Input() set isDisabled(value: boolean){
    this._isDisabled = value;
    this.checkInputDisabled();
  }

  @Input() formGroup: FormGroup = new FormGroup([]);

  @Input() set config(configIn: IAvSettingsItemConfig) {
    this.configInt = configIn;
    if ((this.configInt.options.length === 0) &&
      (this.configInt.type === AvProdSettingsType.selectComboNumber) &&
      (this.configInt.step !== 0)) {
      for (let i: number = 0; this.configInt.min + (this.configInt.step * i) <= this.configInt.max; i++) {
        const VALUE: number = this.configInt.min + (this.configInt.step * i);
        if (VALUE <= this.configInt.max){
          const OPTION: IAvSettingSelectOption = {
            label: VALUE.toString(),
            labelTranslate: VALUE.toString(),
            valueNumber: VALUE,
            valueStr: VALUE.toString()
          }
          this.configInt.options.push(OPTION);
        }
      }
    }

    if (this.colorValueSubscription !== undefined) this.colorValueSubscription.unsubscribe();
    if (this.configInt.type === AvProdSettingsType.color) {
      if (this.configInt.options.length === 0){
        this.configInt.options = AV_PROD_OPTIONS_COLOR;
      }
      this.colorValueSubscription = this.formControl.valueChanges.subscribe(event => this.onColorSetValue(event));
    }
  }

  @Output() settingChange = new EventEmitter<IAvSettingsItemConfig>();
  @Output() settingTouched = new EventEmitter<IAvSettingsItemConfig>();
  @Output() settingFocus = new EventEmitter<IAvSettingsItemConfig>();
  @Output() settingSelectSearchAdd = new EventEmitter<IAvSettingsItemConfigEventString>();
  @Output() settingSelectSearchChange = new EventEmitter<IAvSettingsItemConfigEventString>();

  constructor(@Inject(DOCUMENT) private dom: Document,
              protected translate: TranslateService) {
    // do nothing
  }

  ngOnInit(): void {
    const MY_FORM_CONTROL: AbstractControl | null = this.formGroup.controls[this.configInt.id];
    if (MY_FORM_CONTROL !== null) {
      this.formControl = MY_FORM_CONTROL;
      if (this.colorValueSubscription !== undefined) this.colorValueSubscription.unsubscribe();
      if (this.configInt.type === AvProdSettingsType.color) {
        this.onColorSetValue(this.formControl.value);
        if (this.colorValueSubscription !== undefined) this.colorValueSubscription.unsubscribe();
        this.colorValueSubscription = this.formControl.valueChanges.subscribe(event => this.onColorSetValue(event));
      }
    }

    this.searchInputSubject$.pipe(
      debounceTime(300),          // Espera 300ms después de la última pulsación
      distinctUntilChanged()      // Solo si el texto es diferente al anterior
    ).subscribe(term => {
      // Cuando el término filtrado está listo, lo emitimos al componente padre
      this.settingSelectSearchChange.emit({ item: this.configInt, data: term});
    });

    this.languageSubscription = this.translate.onLangChange.subscribe(() => this.translateSelectTexts());
    this.translateSelectTexts();
  
    this.checkInputDisabled();
  }

  ngOnDestroy(): void {
    if (this.colorValueSubscription !== undefined) this.colorValueSubscription.unsubscribe();
    this.languageSubscription?.unsubscribe();
  }

  /**
   * Function called when the user changes the value
   */
  protected onControlChange() {
    //console.log('[SettingsItemComponent] onControlChange');
    this.settingChange.emit(this.configInt);
  }

  /**
   * Function called when the user touches the control
   */
  protected onControlTouched() {
    //console.log('[SettingsItemComponent] onControlTouched');
    this.settingTouched.emit(this.configInt);
  }

  /**
   * Function called when the control gets focus
   */
  protected onControlFocus() {
    this.settingFocus.emit(this.configInt);
  }

  protected onColorSetValue(event: any) {
    console.log('[SettingsItemComponent] onColorSetValue ' + JSON.stringify(event));
    if ((typeof (event) === 'string') && event.includes('#')) {
      this.colorText = 'custom';
      this.colorCustom = event;
    } else {
      this.colorText = event;
      this.colorCustom = this.colorText2Code(event);
    }
  }

  protected colorText2Code(color: string): string {
    let ret: string = '#ffffff';
    switch (color){
      case 'white':
        ret = '#ffffff';
        break;
      case 'black':
        ret = '#000000';
        break;
      case 'red':
        ret = '#ff0000';
        break;
      case 'blue':
        ret = '#1d71b8';
        break;
      case 'darkblue':
        ret = '#172039';
        break;
      case 'lightblue':
        ret = '#009fe3';
        break;
      case 'yellow':
        ret = '#ffdc00';
        break;
      case 'green':
        ret = '#00dc1e';
        break;
      case 'orange':
        ret = '#f3920b';
        break;
      case 'gray':
        ret = '#c0c0c0';
        break;
      case 'darkgray':
        ret = '#646464';
        break;
    }
    return ret;
  }

  protected onColorChange() {
    console.log('[SettingsItemComponent] onColorChange');
    if (this.colorText === 'custom') {
      this.colorText = 'custom';
      this.formControl.setValue(this.colorCustom);
    } else {
      this.formControl.setValue(this.colorText);
    }
    this.formControl.markAsDirty();
    this.onControlChange();
  }

  protected onColorChangeCustom() {
    console.log('[SettingsItemComponent] onColorChangeCustom');
    this.colorText = 'custom';
    this.formControl.setValue(this.colorCustom);
    this.formControl.markAsDirty();
    this.onControlChange();
  }

  protected onSliderIncrease() {
    if ((this.configInt.type === 'numberSlider') &&
      (this.configInt.step !== 0)) {
      if (this.formControl.value + this.configInt.step > this.configInt.max) {
        this.formControl.setValue(this.configInt.max);
      } else {
        // Avoid more than 4 decimal digits
        let newValue: number = this.formControl.value + this.configInt.step;
        newValue = (Math.round(newValue * 10000)) / 10000;
        this.formControl.setValue(newValue);
      }
      this.formControl.markAsDirty();
      this.onControlChange();
    }
  }

  protected onSliderDecrease() {
    if ((this.configInt.type === 'numberSlider') &&
      (this.configInt.step !== 0)) {
      if (this.formControl.value - this.configInt.step < this.configInt.min) {
        this.formControl.setValue(this.configInt.min);
      } else {
        // Avoid more than 4 decimal digits
        let newValue: number = this.formControl.value - this.configInt.step;
        newValue = (Math.round(newValue * 10000)) / 10000;
        this.formControl.setValue(newValue);
      }
      this.formControl.markAsDirty();
      this.onControlChange();
    }
  }

  protected onInputScroll(elementId: string){
    // Ojo! Hay que revisar esto y dejar bien para el móvil
    //let el: HTMLElement | null = this.dom.getElementById(elementId);
    //el?.scrollIntoView({behavior: 'smooth'});
  }

  protected checkInputDisabled(){
    if(this._isDisabled === true){
      this.formGroup.controls[this.configInt.id]?.disable();
    }
    else if(this._isDisabled === false){
      this.formGroup.controls[this.configInt.id]?.enable();      
    }
  }

  protected onAddNgSelectOption = (newTag: string) => {
    this.settingSelectSearchAdd.emit({ item: this.configInt, data: newTag});
    // Return undefined to avoid the control adding item by itself
    return false;
  }

  protected onSelectSearch(term: string) {
    this.searchInputSubject$.next(term);
  }

  protected translateSelectTexts() {
    this.translatedSelectNotfound = this.translate.instant('general.selectNotFound');
    this.translatedSelectTypeToSearch = this.translate.instant('general.selectTypeToSearch');
    this.translatedSelectAddTag = this.translate.instant('general.selectAddTag');
  }
}
